import { useMemo, useState } from 'react';

function BitDisplay({ value, bits = 8 }: { value: number; bits?: number }) {
  return (
    <div className="sim-bit-display" style={{ gridTemplateColumns: `repeat(${bits}, minmax(0, 1fr))` }} aria-label={`${value}의 ${bits}비트 이진수`}>
      {Array.from({ length: bits }, (_, index) => {
        const bit = bits - index - 1;
        const on = Boolean(value & (1 << bit));
        return <span key={bit} className={on ? 'on' : ''}><small>{bit}</small>{on ? 1 : 0}</span>;
      })}
    </div>
  );
}

function EightBitSimulator() {
  const [bits, setBits] = useState<4 | 8>(4);
  const [a, setA] = useState(7);
  const [b, setB] = useState(9);
  const [subtract, setSubtract] = useState(false);
  const [counter, setCounter] = useState(0);
  const [address, setAddress] = useState(0);
  const [data, setData] = useState(0x55);
  const [memory, setMemory] = useState<number[]>(() => Array(32).fill(0));
  const [ramMessage, setRamMessage] = useState('주소와 데이터를 정한 뒤 WRITE를 눌러보세요.');

  const maxValue = bits === 4 ? 0x0f : 0xff;
  const maxAddress = bits === 4 ? 15 : 31;

  const calculation = useMemo(() => {
    const raw = subtract ? a - b : a + b;
    const result = raw & maxValue;
    return {
      result,
      zero: result === 0,
      negative: Boolean(result & (1 << (bits - 1))),
      carry: subtract ? a >= b : raw > maxValue,
    };
  }, [a, b, subtract, bits, maxValue]);

  const clampValue = (value: string) => Math.min(maxValue, Math.max(0, Number(value) || 0));

  const changeBits = (nextBits: 4 | 8) => {
    const nextMax = nextBits === 4 ? 0x0f : 0xff;
    const nextAddressMax = nextBits === 4 ? 15 : 31;
    setBits(nextBits);
    setA(value => value & nextMax);
    setB(value => value & nextMax);
    setData(value => value & nextMax);
    setCounter(value => value & nextAddressMax);
    setAddress(value => value & nextAddressMax);
    setRamMessage(`${nextBits}비트 모드로 전환했습니다.`);
  };

  const writeMemory = () => {
    setMemory(values => values.map((value, index) => index === address ? data : value));
    setRamMessage(`WRITE 완료: 주소 ${address} ← 0x${data.toString(16).toUpperCase().padStart(2, '0')}`);
  };

  const readMemory = () => {
    const value = memory[address] & maxValue;
    setData(value);
    setRamMessage(`READ 완료: 주소 ${address} → 0x${value.toString(16).toUpperCase().padStart(2, '0')}`);
  };

  return (
    <section className="eight-bit-simulator" aria-label="8비트 컴퓨터 웹 모듈 시험기">
      <div className="simulator-header">
        <span>INTERACTIVE TEST</span>
        <h3>배선 전에 기준값 확인하기</h3>
        <p>1단계 4비트와 2단계 8비트의 기준값을 실물 LED와 비교하세요.</p>
      </div>

      <div className="sim-mode-switch" aria-label="시험기 비트 수 선택">
        <button className={bits === 4 ? 'active' : ''} onClick={() => changeBits(4)}>1단계 · 4비트</button>
        <button className={bits === 8 ? 'active' : ''} onClick={() => changeBits(8)}>2단계 · 8비트</button>
      </div>

      <div className="simulator-grid">
        <article className="simulator-card">
          <h4>8비트 ALU</h4>
          <div className="sim-input-row">
            <label>A<input aria-label="ALU A 값" type="number" min="0" max={maxValue} value={a} onChange={event => setA(clampValue(event.target.value))} /></label>
            <label>B<input aria-label="ALU B 값" type="number" min="0" max={maxValue} value={b} onChange={event => setB(clampValue(event.target.value))} /></label>
          </div>
          <div className="sim-segmented">
            <button className={!subtract ? 'active' : ''} onClick={() => setSubtract(false)}>ADD</button>
            <button className={subtract ? 'active' : ''} onClick={() => setSubtract(true)}>SUB</button>
          </div>
          <BitDisplay value={calculation.result} bits={bits} />
          <p className="sim-result"><b>{a} {subtract ? '−' : '+'} {b} = {calculation.result}</b><code>0x{calculation.result.toString(16).toUpperCase().padStart(2, '0')}</code></p>
          <div className="sim-flags"><span className={calculation.zero ? 'on' : ''}>Z</span><span className={calculation.negative ? 'on' : ''}>N</span><span className={calculation.carry ? 'on' : ''}>C</span></div>
        </article>

        <article className="simulator-card">
          <h4>{bits === 4 ? '4비트' : '5비트'} 프로그램 카운터</h4>
          <BitDisplay value={counter} bits={bits === 4 ? 4 : 5} />
          <p className="sim-counter-value">현재 주소 <b>{counter}</b> / {maxAddress}</p>
          <div className="sim-button-row">
            <button onClick={() => setCounter(value => (value + 1) & maxAddress)}>CLOCK +1</button>
            <button onClick={() => setCounter(0)}>RESET</button>
          </div>
          <small>{maxAddress}에서 한 번 더 누르면 0으로 돌아와야 합니다.</small>
        </article>

        <article className="simulator-card ram-card">
          <h4>{bits === 4 ? '16 × 4 사용 범위' : '32 × 8 전체'} RAM</h4>
          <div className="sim-input-row ram-inputs">
            <label>주소<input aria-label="RAM 주소" type="number" min="0" max={maxAddress} value={address} onChange={event => setAddress(Math.min(maxAddress, Math.max(0, Number(event.target.value) || 0)))} /></label>
            <label>데이터<input aria-label="RAM 데이터" type="number" min="0" max={maxValue} value={data} onChange={event => setData(clampValue(event.target.value))} /></label>
          </div>
          <BitDisplay value={data} bits={bits} />
          <div className="sim-button-row">
            <button onClick={writeMemory}>WRITE</button>
            <button onClick={readMemory}>READ</button>
          </div>
          <p className="ram-test-message" aria-live="polite">{ramMessage}</p>
          <div className="sim-memory-grid">
            {memory.slice(0, maxAddress + 1).map((value, index) => <button key={index} className={index === address ? 'active' : ''} onClick={() => setAddress(index)} title={`주소 ${index}, 값 ${value & maxValue}`}><small>{index}</small><b>{(value & maxValue).toString(16).toUpperCase().padStart(bits === 4 ? 1 : 2, '0')}</b></button>)}
          </div>
        </article>
      </div>
    </section>
  );
}

export default EightBitSimulator;
